import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  OcorrenciaSyncRequest,
  RegistroFila,
  isSincronizavel,
  registroParaRequest,
} from '../models/alocacao.model';

export interface SyncResponse {
  success: boolean;
  message: string;
}

/** Resultado do registro de uma ação. */
export interface RegistroResultado {
  /** true = enviado ao backend agora; false = ficou pendente na fila. */
  sincronizado: boolean;
  /** quantidade de registros pendentes após a operação. */
  pendentes: number;
  /** true quando ficou pendente por falha de envio (e não por estar offline). */
  erro?: boolean;
  /** true quando a ação não tem correspondente no backend (não foi registrada). */
  naoSuportado?: boolean;
}

const STORAGE_KEY = 'fila_ocorrencias';

/**
 * Gerencia a fila de ocorrências registradas em campo:
 * - Persiste a fila em localStorage (sobrevive a reload / queda de conexão);
 * - Sincroniza com o backend via POST /bpv/events/ocorrencias/sync.
 *
 * O header Authorization é injetado automaticamente pelo AuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class OcorrenciaSyncService {
  private readonly API_URL = environment.API_URL;

  private readonly filaSubject = new BehaviorSubject<RegistroFila[]>(this.carregarFila());
  /** Fila reativa — assine para refletir a contagem de pendentes na UI. */
  readonly fila$ = this.filaSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  get fila(): RegistroFila[] {
    return this.filaSubject.value;
  }

  /** Adiciona um registro à fila e persiste. */
  enfileirar(registro: RegistroFila): void {
    const novaFila = [...this.filaSubject.value, registro];
    this.persistir(novaFila);
  }

  /**
   * Registra uma ação. Se houver internet, envia imediatamente ao backend
   * junto com tudo que estiver pendente na fila (de uma vez só). Se estiver
   * offline ou o envio falhar, o registro vai para a fila para sincronização
   * posterior.
   */
  registrar(registro: RegistroFila): Observable<RegistroResultado> {
    if (!isSincronizavel(registro.eventTypeEnum)) {
      // Sem correspondente no backend — não enfileira para não travar o lote.
      return of({ sincronizado: false, pendentes: this.fila.length, naoSuportado: true });
    }

    if (!navigator.onLine) {
      this.enfileirar(registro);
      return of({ sincronizado: false, pendentes: this.fila.length });
    }

    const lote = [...this.filaSubject.value, registro];
    return this.enviar(lote).pipe(
      map(() => {
        this.persistir([]);
        return { sincronizado: true, pendentes: 0 } as RegistroResultado;
      }),
      catchError(() => {
        this.enfileirar(registro);
        return of({ sincronizado: false, pendentes: this.fila.length, erro: true } as RegistroResultado);
      }),
    );
  }

  /**
   * Envia a fila inteira ao backend. Em caso de sucesso, limpa a fila local.
   */
  sincronizar(): Observable<SyncResponse> {
    return this.enviar(this.filaSubject.value).pipe(tap(() => this.persistir([])));
  }

  private enviar(lote: RegistroFila[]): Observable<SyncResponse> {
    const payload: OcorrenciaSyncRequest[] = lote
      .map(registroParaRequest)
      .filter((r): r is OcorrenciaSyncRequest => r !== null);
    return this.http.post<SyncResponse>(`${this.API_URL}/bpv/events/ocorrencias/sync`, payload);
  }

  limparFila(): void {
    this.persistir([]);
  }

  private persistir(fila: RegistroFila[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fila));
    this.filaSubject.next(fila);
  }

  private carregarFila(): RegistroFila[] {
    try {
      const bruto = localStorage.getItem(STORAGE_KEY);
      return bruto ? (JSON.parse(bruto) as RegistroFila[]) : [];
    } catch {
      return [];
    }
  }
}
